import {
  Signature,
  SignatureDisplayName,
  SignatureEnum,
} from "pmt-types/src/enums/Signature.ts";
import {
  Certificate,
  CertificateSchema,
} from "pmt-types/src/models/Certificate.ts";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import CoveragePicker from "../../components/form/coveragePicker.tsx";
import EnumSelect from "../../components/form/enumSelect.tsx";
import FormInput from "../../components/form/formInput.tsx";
import PmtForm from "../../components/form/pmtForm.tsx";
import FormRoot from "../../components/form/style/formRoot.tsx";
import FormSection from "../../components/form/style/formSection.tsx";
import TextArea from "../../components/form/textArea.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import NotFoundPage from "../../global/notFoundPage.tsx";
import { useMutationAddCertificate } from "../../hooks/mutations/useMutationAddCertificate.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import { useQueryGetEnrolleeById } from "../../hooks/queries/useQueryGetEnrolleeById.ts";
import { useQueryGetProjectById } from "../../hooks/queries/useQueryGetProjectById.ts";
import { useQueryGetSourceById } from "../../hooks/queries/useQueryGetSourceById.ts";

export default function AddCertificate() {
  const navigate = useNavigate();
  const { sourceId, projectId, enrolleeId, certificateId } = useParams();
  const queryClient = useQueryClient();

  const { data: enrollee, isFetching: isEnrolleeFetching } =
    useQueryGetEnrolleeById(sourceId, projectId, enrolleeId);
  const { data: project, isFetching: isProjectFetching } =
    useQueryGetProjectById(sourceId, projectId);
  const { data: source, isFetching: isSourceFetching } = useQueryGetSourceById(
    project?.source.id,
  );

  const { mutate, isLoading: isSubmitting } = useMutationAddCertificate();

  if (certificateId == null) {
    return <NotFoundPage />;
  }

  if (isEnrolleeFetching || isSourceFetching || isProjectFetching) {
    return <LoadingSpinner />;
  }

  if (source == null || project == null || enrollee == null) {
    return <NotFoundPage />;
  }

  const defaultCertificateValues: Partial<Certificate> = {
    id: "new",
    name: enrollee.id,
    source,
    project,
    enrollee,
    signature: project.signature,
    descriptionOfOperations: `The Certificate Holder is an Insured under the Owner Controlled Insurance Program as specified in the contract documents. Project Name: ${project.name}`,
    policies: {
      generalLiability: source.generalLiability,
      excessLiabilities: source.excessLiabilities ?? [],
    },
  };

  return (
    <PmtForm<Certificate>
      schema={CertificateSchema}
      pageTitle={`${source.name} / ${project.name} / ${enrollee.name} / New Certificate`}
      defaultValues={defaultCertificateValues}
      submitMutation={mutate}
      onSave={async () => {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GetEnrolleeCertificates, enrolleeId],
        });
        navigate(
          `/sources/${sourceId}/projects/${projectId}/enrollees/${enrolleeId}/details`,
        );
      }}
      onCancel={() => navigate(-1)}
      isSubmitting={isSubmitting}
      renderForm={(form) => {
        form.register("source");
        form.register("project");
        form.register("enrollee");
        return (
          <FormRoot>
            <FormSection label={"Details"}>
              <FormInput
                name="effectiveDate"
                label="Effective Date"
                type="date"
              />
              <FormInput
                name="expirationDate"
                label="Expiration Date"
                type="date"
              />
              <EnumSelect
                name="signature"
                label="Signature"
                data={Signature.options}
                formatOption={(option) =>
                  SignatureDisplayName[option as SignatureEnum]
                }
              />
              <TextArea
                name={"descriptionOfOperations"}
                label={"Description of Operations"}
              />
              <CoveragePicker name={"policies"} source={source} />
            </FormSection>
          </FormRoot>
        );
      }}
    />
  );
}
